import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PlanPrice {
  priceId: string;
  name: string;
  amount: number; // cents
  currency: string;
  interval: string;
  intervalCount: number;
}

export type PlanKey =
  | 'student_annual'
  | 'student_monthly'
  | 'mastery_annual'
  | 'mastery_monthly';

// Fallback values (in cents) — used when Stripe is unreachable so UI never breaks.
// MUST stay in sync with live Stripe prices if changed there.
const FALLBACK_PRICES: Record<PlanKey, PlanPrice> = {
  student_annual: { priceId: '', name: 'Student', amount: 20388, currency: 'usd', interval: 'year', intervalCount: 1 },
  student_monthly: { priceId: '', name: 'Student', amount: 1999, currency: 'usd', interval: 'month', intervalCount: 1 },
  mastery_annual: { priceId: '', name: 'Mastery', amount: 29988, currency: 'usd', interval: 'year', intervalCount: 1 },
  mastery_monthly: { priceId: '', name: 'Mastery', amount: 2999, currency: 'usd', interval: 'month', intervalCount: 1 },
};

const currencyFmt = (currency = 'usd') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  });

/** Format cents → "$29.99" */
export function formatPrice(amountInCents: number, currency = 'usd'): string {
  return currencyFmt(currency).format(amountInCents / 100);
}

/** Compute monthly equivalent of an annual price → "$16.99" */
export function monthlyEquivalent(annualAmountInCents: number, currency = 'usd'): string {
  const monthlyCents = Math.round(annualAmountInCents / 12);
  return currencyFmt(currency).format(monthlyCents / 100);
}

async function fetchPlanPrices(): Promise<Record<string, PlanPrice>> {
  const { data, error } = await supabase.functions.invoke('get-plan-prices');
  if (error) throw error;
  if (!data?.prices) throw new Error('No prices returned');
  return data.prices as Record<string, PlanPrice>;
}

export function usePlanPrices() {
  const query = useQuery({
    queryKey: ['plan-prices'],
    queryFn: fetchPlanPrices,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  // Merge live data with fallbacks so consumers always have a complete set
  const prices: Record<PlanKey, PlanPrice> = {
    student_annual: (query.data?.student_annual || query.data?.student || FALLBACK_PRICES.student_annual) as PlanPrice,
    student_monthly: (query.data?.student_monthly || FALLBACK_PRICES.student_monthly) as PlanPrice,
    mastery_annual: (query.data?.mastery_annual || FALLBACK_PRICES.mastery_annual) as PlanPrice,
    mastery_monthly: (query.data?.mastery_monthly || FALLBACK_PRICES.mastery_monthly) as PlanPrice,
  };

  return {
    prices,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
